import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { sanitizeHtml } from '../utils/sanitizeHtml'

interface CellTooltipProps {
  children: React.ReactNode
  content: string | React.ReactNode
  className?: string
  /**
   * Fixed width in px, kept inside the viewport. Without it the tooltip
   * shrinks to the space right of the cell, which squeezes wide content
   * near the right edge into a tall column (#707).
   */
  width?: number
}

export default function CellTooltip({ children, content, className = '', width }: CellTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<'top' | 'bottom'>('top')
  const [coordinates, setCoordinates] = useState({ top: 0, left: 0 })
  const [arrowLeft, setArrowLeft] = useState<number | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const fixedWidth = width ? Math.min(width, window.innerWidth - 16) : undefined

  useEffect(() => {
    if (isVisible && wrapperRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect()
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200
      const center = wrapperRect.left + wrapperRect.width / 2
      let left = center
      if (fixedWidth) {
        left = Math.min(Math.max(center, 8 + fixedWidth / 2), window.innerWidth - 8 - fixedWidth / 2)
        setArrowLeft(center - (left - fixedWidth / 2))
      }

      // Check if tooltip would overflow viewport top
      if (wrapperRect.top - tooltipHeight - 8 < 0) {
        // Position below
        setPosition('bottom')
        setCoordinates({
          top: wrapperRect.bottom + 8,
          left,
        })
      } else {
        // Position above
        setPosition('top')
        setCoordinates({
          top: wrapperRect.top - 8,
          left,
        })
      }
    }
  }, [isVisible, fixedWidth])

  const tooltipContent = isVisible && content && (
    <div
      ref={tooltipRef}
      className={`fixed z-9999 px-3 py-2 text-sm bg-gray-900 dark:bg-gray-800 text-white rounded-lg shadow-xl whitespace-normal min-w-[200px] ${fixedWidth ? '' : 'max-w-[400px]'} pointer-events-none`}
      style={{
        top: position === 'top' ? `${coordinates.top}px` : `${coordinates.top}px`,
        left: `${coordinates.left}px`,
        width: fixedWidth,
        transform: position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      }}
    >
      {/* Arrow */}
      <div
        className={`absolute left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 ${
          position === 'top' ? 'bottom-[-4px]' : 'top-[-4px]'
        }`}
        style={fixedWidth && arrowLeft !== null ? { left: `${arrowLeft}px` } : undefined}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {typeof content === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
        ) : (
          content
        )}
      </div>
    </div>
  )

  return (
    <>
      <div
        ref={wrapperRef}
        className={`inline-block ${className}`}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  )
}
